import { Pencil, Trash2, Power, PowerOff, Activity } from 'lucide-react';
import type { Assistant } from '../../types/api';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableHeader,
  TableRow,
  Button,
  EmptyState,
  Badge,
} from '../ui';

interface AssistantListProps {
  assistants: Assistant[];
  onEdit: (assistant: Assistant) => void;
  onDelete: (assistant: Assistant) => void;
  isLoading?: boolean;
  isFiltered?: boolean;
}

function AssistantList({
  assistants,
  onEdit,
  onDelete,
  isLoading,
  isFiltered,
}: AssistantListProps) {
  const getStatusModeDisplay = (mode: string) => {
    switch (mode) {
      case 'always_active':
        return 'Sempre Ativo';
      case 'always_inactive':
        return 'Sempre Inativo';
      case 'auto':
        return 'Automático';
      case 'triggered':
        return 'Ativado por Gatilho';
      default:
        return mode;
    }
  };

  const getStatusModeBadge = (mode: string) => {
    switch (mode) {
      case 'always_active':
        return <Badge variant="success">Sempre Ativo</Badge>;
      case 'always_inactive':
        return <Badge variant="error">Sempre Inativo</Badge>;
      case 'auto':
        return <Badge variant="info">Automático</Badge>;
      case 'triggered':
        return <Badge variant="warning">Ativado por Gatilho</Badge>;
      default:
        return <Badge>{mode}</Badge>;
    }
  };

  const getCurrentStatusIcon = (status?: string) => {
    if (status === 'active') {
      return <Power className="h-4 w-4 text-green-500" title="Ativo" />;
    } else if (status === 'inactive') {
      return <PowerOff className="h-4 w-4 text-red-500" title="Inativo" />;
    } else {
      return <Activity className="h-4 w-4 text-gray-400" title="Status desconhecido" />;
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (assistants.length === 0) {
    return (
      <EmptyState
        icon={<Activity className="h-12 w-12" />}
        title={isFiltered ? 'Nenhum assistente encontrado' : 'Nenhum assistente disponível'}
        description={
          isFiltered
            ? 'Tente ajustar sua pesquisa para encontrar o que procura'
            : 'Crie seu primeiro assistente para começar'
        }
      />
    );
  }

  return (
    <TableContainer>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead>ID</TableHead>
            <TableHead>Nome</TableHead>
            <TableHead>Modelo</TableHead>
            <TableHead>Tipo</TableHead>
            <TableHead>Assinatura</TableHead>
            <TableHead>Modo de Status</TableHead>
            <TableHead>Status Atual</TableHead>
            <TableHead>Integração</TableHead>
            <TableHead className="relative">
              <span className="sr-only">Ações</span>
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {assistants.map((assistant: Assistant) => (
            <TableRow key={assistant.id}>
              <TableCell>{assistant.id}</TableCell>
              <TableCell>{assistant.name}</TableCell>
              <TableCell>{assistant.assistant_model_name || '-'}</TableCell>
              <TableCell>{assistant.type}</TableCell>
              <TableCell>{assistant.signature}</TableCell>
              <TableCell>{getStatusModeBadge(assistant.status_mode)}</TableCell>
              <TableCell>
                <div className="flex items-center">
                  {getCurrentStatusIcon(assistant.current_status)}
                  <span className="ml-2">
                    {assistant.current_status === 'active'
                      ? 'Ativo'
                      : assistant.current_status === 'inactive'
                      ? 'Inativo'
                      : 'Desconhecido'}
                  </span>
                </div>
              </TableCell>
              <TableCell>{assistant.integration_description || '-'}</TableCell>
              <TableCell className="text-right space-x-2">
                <Button variant="ghost" onClick={() => onEdit(assistant)} title="Editar">
                  <Pencil className="h-4 w-4" />
                </Button>
                <Button variant="ghost" onClick={() => onDelete(assistant)} title="Excluir">
                  <Trash2 className="h-4 w-4 text-red-600" />
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}

export default AssistantList;
